import {
  VESSEL_SEARCHER_FILTER_BOUNDS,
} from "../constants/vessel-searcher.constants";
import type {
  VesselSearcherFilterOptions,
  VesselSearcherOptionField,
  VesselSearcherRequest,
  VesselSearcherResponse,
  VesselSearcherVessel,
} from "../models/vessel-searcher.models";

const createVesselSearcherFixture = (
  vessel: Pick<VesselSearcherVessel, "imo" | "vesselName" | "buildDate" | "keelLaidDate" | "dwt"> &
    Partial<Omit<VesselSearcherVessel, "imo" | "vesselName" | "buildDate" | "keelLaidDate" | "dwt">>,
): VesselSearcherVessel => ({
  design: null,
  yard: null,
  country: null,
  engineType: null,
  lastInspection: null,
  useScrubber: null,
  lastFixtureDate: null,
  imosLink: null,
  mcrIhs: null,
  engineBuilder: null,
  lbp: null,
  breadth: null,
  depth: null,
  draft: null,
  eexi: null,
  evdi: null,
  ghgType: null,
  hasVoyageInImos: false,
  ...vessel,
});

export const VESSEL_SEARCHER_MOCK_VESSELS: VesselSearcherVessel[] = [
  createVesselSearcherFixture({
    imo: "9593505", vesselName: "PIONEERING SPIRIT", buildDate: "2014-11-01", keelLaidDate: "2011-12-01", dwt: 499125,
    yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "20V32/44CR", lastInspection: "2024-11-11",
    mcrIhs: 89600, engineBuilder: "MAN B&W Diesel AG - Augsburg", lbp: 370, breadth: 123.75, depth: 29, draft: 19.85,
  }),
  createVesselSearcherFixture({
    imo: "9246633", vesselName: "SA OCEANIA", buildDate: "2003-04-01", keelLaidDate: "2002-06-01", dwt: 441585,
    yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "9RTA84T-D", lastInspection: "2026-04-08",
    mcrIhs: 36900, engineBuilder: "Doosan Engine Co Ltd - South Korea", lbp: 366, breadth: 68, depth: 34, draft: 24.52,
  }),
  createVesselSearcherFixture({
    imo: "9235268", vesselName: "SA EUROPE", buildDate: "2002-11-01", keelLaidDate: "2002-04-01", dwt: 441561,
    yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "9RTA84T-D", lastInspection: "2025-11-18",
    mcrIhs: 36900, engineBuilder: "Doosan Engine Co Ltd - South Korea", lbp: 366, breadth: 68, depth: 34, draft: 24.52,
    eexi: 0,
  }),
  createVesselSearcherFixture({
    imo: "9224752", vesselName: "FSO ASIA", buildDate: "2002-03-01", keelLaidDate: "2001-06-01", dwt: 432023,
    yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "9RTA84T-D", lastInspection: "2003-07-29",
    mcrIhs: 36445, engineBuilder: "Doosan Engine Co Ltd - South Korea", lbp: 366, breadth: 68, depth: 34, draft: 24.52,
  }),
  createVesselSearcherFixture({
    imo: "9224764", vesselName: "FSO AFRICA", buildDate: "2002-06-01", keelLaidDate: "2001-09-01", dwt: 432023,
    yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "9RTA84T-D", lastInspection: "2008-11-26",
    mcrIhs: 36938, engineBuilder: "Doosan Engine Co Ltd - South Korea", lbp: 364, breadth: 68, depth: 34.24, draft: 24.52,
  }),
  createVesselSearcherFixture({
    imo: "9575450", vesselName: "SEA QINGDAO", buildDate: "2012-04-01", keelLaidDate: "2011-09-01", dwt: 403880,
    design: "STX 4000C", yard: "STX Offshore & Shipbuilding - Jinhae", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2023-08-16", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.03, evdi: 2.03, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575515", vesselName: "SEA MARANHAO", buildDate: "2013-08-01", keelLaidDate: "2012-11-01", dwt: 403844,
    design: "STX 4000C", yard: "STX Offshore & Shipbuilding - Jinhae", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2026-04-07", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.03, evdi: 2.03, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575448", vesselName: "SEA BEIJING", buildDate: "2011-09-01", keelLaidDate: "2011-04-01", dwt: 403818,
    design: "STX 4000C", yard: "STX Offshore & Shipbuilding - Jinhae", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2026-01-13", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.04, evdi: 2.04, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575486", vesselName: "SEA FUJIYAMA", buildDate: "2012-11-01", keelLaidDate: "2012-04-01", dwt: 403811,
    design: "STX 4000C", yard: "STX Offshore & Shipbuilding - Jinhae", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2026-02-24", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.03, evdi: 2.03, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575498", vesselName: "SEA TUBARAO", buildDate: "2013-01-01", keelLaidDate: "2012-01-01", dwt: 403784,
    design: "STX 4000C", yard: "STX Dalian Shipbuilding Co Ltd", country: "China", engineType: "7S80ME-C8",
    lastInspection: "2025-06-10", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.03, evdi: 2.03, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575474", vesselName: "SEA INDONESIA", buildDate: "2012-10-01", keelLaidDate: "2011-11-01", dwt: 403727,
    design: "STX 4000C", yard: "STX Dalian Shipbuilding Co Ltd", country: "China", engineType: "7S80ME-C8",
    lastInspection: "2026-04-14", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.04, evdi: 2.04, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575462", vesselName: "SEA ESPIRITO SANTO", buildDate: "2012-09-01", keelLaidDate: "2011-07-01", dwt: 403627,
    design: "STX 4000C", yard: "STX Dalian Shipbuilding Co Ltd", country: "China", engineType: "7S80ME-C8",
    lastInspection: "2025-12-22", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.04, evdi: 2.04, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9575503", vesselName: "SEA PONTA DA MADEIRA", buildDate: "2016-09-01", keelLaidDate: "2012-09-01", dwt: 403508,
    design: "STX 4000C", yard: "STX Dalian Shipbuilding Co Ltd", country: "China", engineType: "7S80ME-C8",
    lastInspection: "2025-07-31", mcrIhs: 29260, engineBuilder: "STX Heavy Industries Co Ltd - Changwon", lbp: 353.5,
    breadth: 65, depth: 30.5, draft: 23.02, eexi: 2.07, evdi: 2.07, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9593957", vesselName: "SHANDONG DA ZHI", buildDate: "2012-07-01", keelLaidDate: "2011-12-01", dwt: 402387,
    design: "DSME 4000C", yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2025-09-22", mcrIhs: 29260, engineBuilder: "Hyundai Heavy Industries Co Ltd - South Korea", lbp: 350,
    breadth: 65, depth: 30.4, draft: 23, eexi: 2.04, evdi: 2.04, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9572331", vesselName: "ORE ITALIA", buildDate: "2011-10-01", keelLaidDate: "2011-06-01", dwt: 402380,
    design: "DSME 4000C", yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2020-11-26", mcrIhs: 29260, engineBuilder: "Hyundai Heavy Industries Co Ltd - South Korea", lbp: 350,
    breadth: 65, depth: 30.4, draft: 23.02, eexi: 2.06, evdi: 2.1, ghgType: "EEXI",
  }),
  createVesselSearcherFixture({
    imo: "9488918", vesselName: "ORE BRASIL", buildDate: "2011-03-01", keelLaidDate: "2010-11-01", dwt: 402347,
    design: "DSME 4000C", yard: "Daewoo Shipbuilding & Marine", country: "Korea, South", engineType: "7S80ME-C8",
    lastInspection: "2025-07-10", mcrIhs: 29260, engineBuilder: "Hyundai Heavy Industries Co Ltd - South Korea", lbp: 350,
    breadth: 65, depth: 30.4, draft: 23.02, eexi: 2.08, ghgType: "EVDI",
  }),
  createVesselSearcherFixture({
    imo: "9566526", vesselName: "SAHAM MAX", buildDate: "2013-01-01", keelLaidDate: "2011-11-01", dwt: 400694,
    design: "China Type VLOC", yard: "Jiangsu Rongsheng Shipbuilding", country: "China", engineType: "7RT-flex82T",
    lastInspection: "2025-10-22", useScrubber: true, lastFixtureDate: "2026-07-02", mcrIhs: 29400,
    engineBuilder: "Hudong Heavy Machinery Co Ltd", lbp: 353, breadth: 65, depth: 30.4, draft: 23, eexi: 2.11,
    ghgType: "EEXI", hasVoyageInImos: true,
  }),
  createVesselSearcherFixture({
    imo: "9522972", vesselName: "ORE CHINA", buildDate: "2011-11-01", keelLaidDate: "2010-09-01", dwt: 400606,
    design: "China Type VLOC", yard: "Jiangsu Rongsheng Shipbuilding", country: "China", engineType: "7RT-flex82T",
    lastInspection: "2025-12-01", mcrIhs: 29400, engineBuilder: "Unknown", lbp: 352.95, breadth: 65, depth: 30.4,
    draft: 23, eexi: 2.03, evdi: 2.03, ghgType: "EEXI",
  }),
];

const getSortedTextOptions = (values: Array<string | null>): string[] =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value)))).sort((left, right) =>
    left.localeCompare(right),
  );

const matchesBooleanFilter = (value: boolean | null, filter: "all" | "yes" | "no" | "blank"): boolean =>
  filter === "all" || (filter === "blank" ? value === null : value === (filter === "yes"));

const matchesNumberRange = (value: number | null, min: number | "", max: number | ""): boolean =>
  (min === "" || (value !== null && value >= min)) && (max === "" || (value !== null && value <= max));

const matchesVesselSearcherRequest = (vessel: VesselSearcherVessel, request: VesselSearcherRequest): boolean => {
  const buildMonth = vessel.buildDate?.slice(0, 7) ?? "";
  const filtersBuildDate = request.activeRangeFilters.includes("buildDate");

  return (
    (!request.imo || vessel.imo === request.imo.trim()) &&
    (!request.vesselName || vessel.vesselName === request.vesselName.trim()) &&
    matchesBooleanFilter(vessel.useScrubber, request.useScrubber) &&
    matchesBooleanFilter(vessel.hasVoyageInImos, request.hasVoyageInImos) &&
    (!filtersBuildDate || !request.buildDateFrom || buildMonth >= request.buildDateFrom) &&
    (!filtersBuildDate || !request.buildDateTo || buildMonth <= request.buildDateTo) &&
    (!request.activeRangeFilters.includes("dwt") || matchesNumberRange(vessel.dwt, request.dwt.min, request.dwt.max)) &&
    (!request.activeRangeFilters.includes("lbp") || matchesNumberRange(vessel.lbp, request.lbp.min, request.lbp.max)) &&
    (!request.activeRangeFilters.includes("draft") || matchesNumberRange(vessel.draft, request.draft.min, request.draft.max)) &&
    (!request.activeRangeFilters.includes("depth") || matchesNumberRange(vessel.depth, request.depth.min, request.depth.max)) &&
    (!request.activeRangeFilters.includes("breadth") || matchesNumberRange(vessel.breadth, request.breadth.min, request.breadth.max)) &&
    (request.engineTypes.length === 0 || (vessel.engineType !== null && request.engineTypes.includes(vessel.engineType))) &&
    (request.designs.length === 0 || (vessel.design !== null && request.designs.includes(vessel.design))) &&
    (request.yards.length === 0 || (vessel.yard !== null && request.yards.includes(vessel.yard))) &&
    (request.countries.length === 0 || (vessel.country !== null && request.countries.includes(vessel.country)))
  );
};

const getMockFilterOptions = (request: VesselSearcherRequest): VesselSearcherFilterOptions => {
  const matchingValues = (
    filterKey: "engineTypes" | "designs" | "yards" | "countries",
    vesselKey: "engineType" | "design" | "yard" | "country",
  ) => getSortedTextOptions(
    VESSEL_SEARCHER_MOCK_VESSELS
      .filter((vessel) => matchesVesselSearcherRequest(vessel, { ...request, [filterKey]: [] }))
      .map((vessel) => vessel[vesselKey]),
  );

  return {
    engineTypes: matchingValues("engineTypes", "engineType"),
    designs: matchingValues("designs", "design"),
    yards: matchingValues("yards", "yard"),
    countries: matchingValues("countries", "country"),
  };
};

export const getMockVesselSearcherOptions = (
  field: VesselSearcherOptionField,
  search: string,
  request: VesselSearcherRequest,
): string[] => {
  const normalizedSearch = search.trim().toLowerCase();
  return getSortedTextOptions(
    VESSEL_SEARCHER_MOCK_VESSELS
      .filter((vessel) => matchesVesselSearcherRequest(vessel, { ...request, [field]: "" }))
      .map((vessel) => vessel[field]),
  )
    .filter((value) => value.toLowerCase().includes(normalizedSearch))
    .slice(0, 50);
};

const sortVesselSearcherFixtures = (
  left: VesselSearcherVessel,
  right: VesselSearcherVessel,
  request: VesselSearcherRequest,
): number => {
  const leftValue = left[request.sortBy];
  const rightValue = right[request.sortBy];

  if (leftValue === null) {
    return rightValue === null ? 0 : 1;
  }

  if (rightValue === null) {
    return -1;
  }

  const comparison = typeof leftValue === "string"
    ? leftValue.localeCompare(String(rightValue))
    : Number(leftValue) - Number(rightValue);

  const directedComparison = request.sortDirection === "asc" ? comparison : -comparison;
  return directedComparison || left.vesselName.localeCompare(right.vesselName) || left.imo.localeCompare(right.imo);
};

export const getMockVesselSearcherResponse = (request: VesselSearcherRequest): VesselSearcherResponse => {
  const filteredVessels = VESSEL_SEARCHER_MOCK_VESSELS
    .filter((vessel) => matchesVesselSearcherRequest(vessel, request))
    .sort((left, right) => sortVesselSearcherFixtures(left, right, request));

  return {
    vessels: filteredVessels.slice(request.offset, request.offset + request.limit),
    total: filteredVessels.length,
    offset: request.offset,
    limit: request.limit,
    filterOptions: getMockFilterOptions(request),
    filterBounds: VESSEL_SEARCHER_FILTER_BOUNDS,
  };
};
